import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, List as ListComponent, useDrawer, Confirm } from 'src/mtska-frontend-app-component';
import { useGridApiRef } from '@mui/x-data-grid';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { AppContext, useSettingContextInComponent } from 'src/mtska-frontend-app-scaffolding';
import { useLoadAll } from 'src/mtska-frontend-data-provider/hooks/useLoadAll.hooks';
import OperatorAssignation from '../externalbuttons/OperatorAssignation';
import OperatorAutoAssignation from '../externalbuttons/OperatorAutoAssignation';

const dataProviderName = 'campaigns/campaignlead';
const moduleName = "AudienceLeads";
const wizardAction = 'destroy';
const getActionPayload = (res) => res?.item?.data ?? res?.data ?? res?.item ?? res ?? {};

const CAP_CAMPAIGN_OPERATOR_ASSOCIATION = 'campaigns/campaign.operatorassociation';
const CAP_CAMPAIGN_OPERATOR_AUTO_ASSOCIATION = 'campaigns/campaign.operatorautoassociation';

/** Backend legacy key "campaignleads" is not a registered drawer path ({module}/{path}); map to the wizard that loads campaigns/campaignlead by id. */
const resolveDrawerRoute = (drawerRoute) => {
	if (drawerRoute === 'campaignleads') {
		return 'campaignmanagement/campaignactivitycreate';
	}
	return drawerRoute;
};

const CampaignLeadList = (params) => {
	const { t } = useTranslation();
	const { userCapabilities, appConfiguration } = useContext(AppContext);
	const { filterModel } = useSettingContextInComponent(dataProviderName);
	const { activate } = useDrawer();
	const [importerVersion] = useState(appConfiguration.importer.version ?? null);

	const apiRef = useGridApiRef();
	const [selected, setSelected] = useState([]);
	const getSelected = () => [...apiRef.current.getSelectedRows()].map(([name]) => name);
	const handleRowSelection = (newSelection) => {
		setSelected(newSelection);
		params.rowSelectionExternalListener && params.rowSelectionExternalListener(newSelection);
	};

	const [confirmDelete, setConfirmDelete] = useState();
	const handleDelete = () => {
		setConfirmDelete(
			<Confirm
				oklabel={t("Delete")}
				okcallable={async () => {
					doAction && await doAction({ action: wizardAction, model: dataProviderName, data: getSelected() });
					setConfirmDelete(null);
					apiRef.current.setRowSelectionModel([]);
					loadAll();
				}}
				kolabel={t("Cancel")}
				kocallable={() => { setConfirmDelete(null) }}
				title={t("Items elimination")}
			>
				{t("Are you sure to remove the selected elements?")}
			</Confirm>
		);
	};

	const { doAction, loadAll, items, total, loading, reloadCounter } = useDataProvider(dataProviderName);
	const [listFilterPageSort, setListFilterPageSort] = useState();
	const handleFilterPageSortChange = (model) => setListFilterPageSort(model);

	useLoadAll({ listFilterPageSort, loadAll, params, reloadCounter });

	const handleRowDoubleclick = (row) => {
        const rel = params.relation;
        const openDetail =
            Boolean(rel?.drawerRoute) && rel.drawerRouteActive !== false;
        if (openDetail) {
            const routeName = resolveDrawerRoute(rel.drawerRoute);
            activate(routeName, { id: row.id });
        }
		// non vogliamo il click
	};

    const handleActionDone = (res) => {
        loadAll();
        const payload = getActionPayload(res);
        const relation = params?.relation;
        const values = relation?.getValues?.();
        const campaignLeads = values?.campaignLeads;
        const associatedLeads = Array.isArray(payload?.campaign_lead_ids) ? payload.campaign_lead_ids : [];

        if (!Array.isArray(campaignLeads) || associatedLeads.length === 0) {
            return;
        }

        campaignLeads.forEach((cl) => {
            if (associatedLeads.includes(cl._id)) {
                cl.operator_id = true;
            }
        });
    }

	const externalButtons = params?.relation?.externalButtons ?? [];
	const rightButtons = [];
	if (externalButtons.includes('operatorAssignationButton') && selected.length > 0 && userCapabilities?.[CAP_CAMPAIGN_OPERATOR_ASSOCIATION]) {
		rightButtons.push(<OperatorAssignation key="assign" selected={selected} campaignId={params?.relation?.campaignId} setSelected={setSelected} />);
	}
	if (externalButtons.includes('operatorAutoAssignationButton') && params?.relation?.campaignId && userCapabilities?.[CAP_CAMPAIGN_OPERATOR_AUTO_ASSOCIATION]) {
		rightButtons.push(<OperatorAutoAssignation key="autoAssign" campaignId={params?.relation?.campaignId} setSelected={setSelected} onActionDone={handleActionDone} />);
	}

	return (
		<>
			{userCapabilities && userCapabilities[dataProviderName + '.list'] && (
				<ListComponent
					{...params}
					onRowDoubleClick={(userCapabilities && userCapabilities[dataProviderName + '.view']) ? handleRowDoubleclick : () => { }}
					onFilterPageSortChange={handleFilterPageSortChange}
					items={items}
					total={total}
					loading={loading}
					apiRef={apiRef}
					enableSelection
					rightButtons={rightButtons}
					dataProviderName={dataProviderName}
					title={moduleName}
					rowSelectionExternalListener={handleRowSelection}
					doAction={doAction}
					filters={params.filters}
					columnsNeeded={[
						"_related_audienceLead_fullName",
						"_related_operator_fullName",
						"_related_operator_role",
						"_related_lastActivity_outcome_name",
						"_related_lastActivity_executed_at",
						"_related_audienceLead_cities",
						"_related_audienceLead_type",
						"_related_audienceLead_source",
						"_related_audienceLead_resCity",
						"_related_audienceLead_kam",
						"_related_audienceLead_privacyStatus",
					]}
					selectionModel={selected}
				/>
			)}
			{confirmDelete}
		</>
	);
};

export default CampaignLeadList;
